import { useState } from 'react';

import { ErrorMessage } from '../Errors/ErrorMessage';

import { HowItWorks } from './HowItWorks';
import { Legal } from './Legal';
import { LocationSearch } from './LocationSearch';
import { Logo } from './Logo';
import { RouteComparison } from './RouteComparison';
import { RouteSelection } from './RouteSelection';
import { ActiveStep, RouteSteps } from './RouteSteps';
import { SearchSummary } from './SearchSummary';
import { ShareDirections } from './ShareDirections';
import { TravelTime } from './TravelTime';

import { Accordion, AccordionContent, AccordionItem, AccordionBasicTrigger } from '@/components/ui/accordion';
import { Button } from '@/components/ui/button';
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from '@/components/ui/drawer';
import { useConfigStore } from '@/store/ConfigStore';
import { useSearchStore } from '@/store/SearchStore';

export const DesktopPanel = () => {
  const { selectedRoute, directions } = useSearchStore();
  return (
    <div className="lt-lg:hidden z-10 flex h-full w-sm shrink-0 flex-col gap-4 overflow-y-auto bg-white px-6 py-4 shadow-lg dark:bg-gray-900 print:flex">
      <div className="flex justify-between">
        <Logo />
        <TravelTime />
      </div>
      <ErrorMessage />
      <LocationSearch />
      <RouteSelection />
      <RouteComparison />
      {directions && !selectedRoute && (
        <div className="flex items-center justify-between">
          <HowItWorks />
          <Legal />
        </div>
      )}
      <Info />
      <SearchSummary />
      {selectedRoute && <hr className="my-5 print:hidden" />}
      <RouteSteps />
    </div>
  );
};

export const MobilePanelTop = () => {
  return (
    <div className="h-sm z-10 flex w-full flex-col gap-4 bg-white shadow-lg lg:hidden dark:bg-gray-900 print:hidden">
      <div className="lt-tiny:px-2 lt-lg:pb-2 flex flex-col gap-4 px-6 py-4">
        <div className="flex justify-between gap-2">
          <Logo />
          <TravelTime />
        </div>
        <ErrorMessage />
        <LocationSearch />
        <SearchSummary />
      </div>
      <ActiveStep />
    </div>
  );
};

export const MobilePanelBottom = () => {
  const { locationSearch, selectedRoute } = useSearchStore();
  const { isMapExpanded } = useConfigStore();
  const shouldHide = locationSearch || (isMapExpanded && selectedRoute);

  if (shouldHide) {
    return null;
  }

  return (
    <div className="z-10 w-full bg-white shadow-lg lg:hidden dark:bg-gray-900 print:hidden">
      <div className="lt-tiny:px-2 lt-tiny:py-4 lt-sm:pt-2 lt-sm:pb-0 flex flex-col gap-4 overflow-hidden p-6">
        <MobileStart />
        <MobileDirections />
        <Info />
      </div>
    </div>
  );
};

const MobileStart = () => {
  const [openValue, setOpenValue] = useState<string | undefined>(undefined);

  const { directions, selectedRoute, highlightedRoute, setSelectedRoute, locationStart, locationEnd } =
    useSearchStore();
  const { airQuality } = highlightedRoute || {};

  if (!airQuality || selectedRoute || !locationStart || !locationEnd) {
    return null;
  }

  return (
    <div className="flex flex-col gap-2 tabular-nums lg:hidden">
      <div className="text-lg font-bold">
        {airQuality?.label_duration} ({airQuality?.label_distance})
      </div>
      {airQuality?.in_modelled_area === 'Y' && airQuality?.no2 !== null && airQuality?.['pm2.5'] !== null && (
        <div className="text-lg">
          NO2 {airQuality.no2.toFixed(1)} µg h/m3 • PM2.5 {airQuality['pm2.5'].toFixed(1)} µg h/m3
        </div>
      )}

      <Accordion type="single" collapsible value={openValue} onValueChange={setOpenValue}>
        <AccordionItem value="item-1">
          <div className="lt-tiny:gap-2 my-4 flex flex-wrap gap-4">
            <Button size="lg" onClick={() => setSelectedRoute(highlightedRoute)}>
              Start
            </Button>
            <AccordionBasicTrigger>
              <Button asChild size="lg" variant="outline">
                <div>{!openValue ? 'More Information' : 'Less Information'}</div>
              </Button>
            </AccordionBasicTrigger>
            <ShareDirections className="lt-tiny:ml-0 ml-auto" />
          </div>
          <AccordionContent className="flex flex-col gap-2">
            <RouteComparison />
            {directions && !selectedRoute && (
              <div className="flex items-center justify-between gap-6">
                <HowItWorks />
                <Legal />
              </div>
            )}
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </div>
  );
};

const MobileDirections = () => {
  const { selectedRoute, highlightedRoute, googleMapLink } = useSearchStore();
  const { airQuality } = highlightedRoute || {};

  if (!selectedRoute) {
    return null;
  }

  return (
    <div className="lt-tiny:gap-1 flex flex-col gap-2 tabular-nums lg:hidden">
      <div className="font-bold sm:text-lg">
        {airQuality?.label_duration} ({airQuality?.label_distance})
      </div>
      {airQuality?.in_modelled_area === 'Y' && airQuality?.no2 !== null && airQuality?.['pm2.5'] !== null && (
        <div className="sm:text-lg">
          NO2 {airQuality.no2.toFixed(1)} µg h/m3 • PM2.5 {airQuality['pm2.5'].toFixed(1)} µg h/m3
        </div>
      )}

      <div className="lt-sm:my-2 my-4 flex flex-wrap justify-between gap-2">
        <Drawer>
          <DrawerTrigger asChild>
            <Button size="lg">Directions</Button>
          </DrawerTrigger>
          <DrawerContent>
            <DrawerHeader>
              <DrawerTitle>Route Steps</DrawerTitle>
              <DrawerDescription>Step by step directions for your selected route</DrawerDescription>
            </DrawerHeader>
            <div className="p-4">
              <RouteSteps />
            </div>
            <DrawerFooter>
              <DrawerClose asChild>
                <Button>Close</Button>
              </DrawerClose>
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
        {googleMapLink && (
          <a href={googleMapLink} target="_blank" rel="noopener noreferrer">
            <Button size="lg" variant="outline">
              Open in Google Maps
            </Button>
          </a>
        )}
      </div>
    </div>
  );
};

const Info = () => {
  const { locationSearch, directions } = useSearchStore();
  const { config } = useConfigStore();
  if (locationSearch || directions) {
    return null;
  }
  return (
    <div className="lt-sm:mb-2 flex flex-col gap-2 lg:mt-6">
      <h1 className="text-2xl text-balance text-[var(--gray-one)] dark:text-white/75">{config?.name}</h1>
      <div className="lt-lg:hidden text-[var(--gray-one)] dark:text-white/75">
        Enter a start and end location to see the route with the best air quality.
      </div>
      <div className="flex items-center justify-between">
        <HowItWorks />
        <Legal />
      </div>
    </div>
  );
};
